/// <amd-module name="Mention/Autocomplete/AutocompleteUtility" />
const MAX_SEARCH_TERMS: number = 10;

export function getMultiTerms(term: string, encodeHtmlChars: boolean): string[] {
    return term
        .split(/\s+/)
        .filter(term => !!term)
        .map(term => (encodeHtmlChars ? htmlEncode(term) : term));
}

export enum SearchBehavior {
    Contains = 0,
    Prefix = 1
}

export interface ISearchResult {
    found: { [key: string]: boolean };
    highlightedSource: string;
}

// Optimize: rewrite using range info and eliminate htmlEncode and RegExp
export function searchString(source: string, terms: string[], searchBehavior: SearchBehavior, highlightSubstitutionPattern?: string): ISearchResult {
    if (typeof source === "undefined" || source === null || terms === null || !terms.length) return { found: {}, highlightedSource: source };
    // for safety, limit to a reasonable number of terms
    terms = terms.slice(0, MAX_SEARCH_TERMS);

    const sourceUpperCase = source.toUpperCase();
    const escapedTerms = terms.map(term => escapeRegExp(term));
    let replaceRegex: RegExp | null = null;
    const found: { [key: string]: boolean } = {};
    let atLeastOneMatch = false;

    // OPTIMIZE HERE (no need to escape regex)
    if (searchBehavior === SearchBehavior.Prefix) {
        terms.forEach(term => {
            found[term] = sourceUpperCase.indexOf(term.toUpperCase()) === 0;
            atLeastOneMatch = atLeastOneMatch || found[term];
        });
        replaceRegex = new RegExp("^(" + escapedTerms.join("|") + ")", "ig");
    } else if (searchBehavior === SearchBehavior.Contains) {
        terms.forEach(term => {
            found[term] = sourceUpperCase.indexOf(term.toUpperCase()) >= 0;
            atLeastOneMatch = atLeastOneMatch || found[term];
        });
        replaceRegex = new RegExp(escapedTerms.join("|"), "ig");
    }

    let highlightedSource = "";
    if (atLeastOneMatch && highlightSubstitutionPattern && replaceRegex) {
        highlightedSource = source.replace(replaceRegex, highlightSubstitutionPattern);
    } else {
        highlightedSource = source;
    }

    return { found, highlightedSource };
}

export function escapeRegExp(str: string): string {
    return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
}

export function htmlEncode(str: string): string {
    // we can write a slightly faster (30%) version that uses a regexp to find and replace
    // all occurences of [&<>"] by their corresponding HTML entities but this approach
    // uses the DOM implementation, so presumably it covers all input/edge cases.

    const div = document.createElement("div");
    div.appendChild(document.createTextNode(str));

    // The trick we are using here doesnt encode quotes. So we have to replace them using regexp search
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
